import { FC, useState } from "react";

import { supportsFulfillment } from "@simplyk/common";

import { LiveFormEditorProvider } from "./common/LiveFormEditorContext";
import { useInitLiveFormFeatureFlags } from "./common/useInitLiveFormFeatureFlags";
import { ETicketStepLivePreview } from "./LivePreview/ETicketLivePreview/ETicketStepLivePreview";
import { TicketingLivePreview } from "./LivePreview/FormLivePreview/TicketingLivePreview";
import { TicketingLiveEditorStepAdditionalDonation } from "./Steps/StepAdditionalDonation/TicketingLiveEditorStepAdditionalDonation";
import { TicketingLiveEditorStepAddOns } from "./Steps/StepAddOns/TicketingLiveEditorStepAddOns";
import { TicketingLiveEditorStepBanner } from "./Steps/StepBanner/TicketingLiveEditorStepBanner";
import { CampaignLifeCycleChoiceProvider } from "./Steps/StepCampaignLifeCycle/Context/CampaignLifeCycleChoiceContext";
import { Illustration as CampaignLifeCycleIllustration } from "./Steps/StepCampaignLifeCycle/Illustration/Illustration";
import { TicketingLiveEditorStepCampaignLifeCycle } from "./Steps/StepCampaignLifeCycle/TicketingLiveEditorStepCampaignLifeCycle";
import { LiveEditorStepBusinessModel } from "./Steps/StepBusinessModel/LiveEditorStepBusinessModel";
import { TicketingLiveEditorStepColor } from "./Steps/StepColor/TicketingLiveEditorStepColor";
import { TicketingLiveEditorStepDate } from "./Steps/StepDate/TicketingLiveEditorStepDate";
import { TicketingLiveEditorStepDiscountCode } from "./Steps/StepDiscountCode/TicketingLiveEditorStepDiscountCode";
import { TicketingLiveEditorStepETicket } from "./Steps/StepETicket/TicketingLiveEditorStepETicket";
import { TicketingLiveEditorStepFundraisingGoal } from "./Steps/StepFundraisingGoal/TicketingLiveEditorStepFundraisingGoal";
import { TicketingLiveEditorStepLocation } from "./Steps/StepLocation/TicketingLiveEditorStepLocation";
import { TicketingLiveEditorStepLogo } from "./Steps/StepLogo/TicketingLiveEditorStepLogo";
import { TicketingLiveEditorStepOrderFulfillment } from "./Steps/StepOrderFulfillment/TicketingLiveEditorStepOrderFulfillment";
import { TicketingLiveEditorStepPrizes } from "./Steps/StepPrizes/TicketingLiveEditorStepPrizes";
import { TicketingLiveEditorStepTickets } from "./Steps/StepTickets/TicketingLiveEditorStepTickets";
import { TicketingLiveEditorStepTitle } from "./Steps/StepTitle/TicketingLiveEditorStepTitle";

import { TicketingOutput } from "@/common/types/trpc";
import { useLogFormEditionStarted } from "@/features/LiveFormEditor/common/amplitude/useLogFormEditionStarted";
import { LiveFormEditorBase } from "@/features/LiveFormEditor/LiveFormEditorBase";
import { TicketingEmailLivePreview } from "@/features/LiveFormEditor/LivePreview/EmailPreview/TicketingEmailLivePreview";
import { MembershipCardLivePreview } from "@/features/LiveFormEditor/LivePreview/ETicketLivePreview/MembershipCardLivePreview";
import { useGetAllTicketingStepsInOrder } from "@/features/LiveFormEditor/Navigation/useGetAllTicketingStepsInOrder";
import { TicketingLiveEditorStepAdvancedSettings } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/TicketingLiveEditorStepAdvancedSettings";
import { TicketingLiveEditorStepAutomatedCommunications } from "@/features/LiveFormEditor/Steps/StepAutomatedCommunications/TicketingLiveEditorStepAutomatedCommunications";
import { StepDescriptionSelector } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionSelector";
import { TicketingLiveEditorStepQuestionsV4 } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/TicketingLiveEditorStepQuestionsV4";
import { TicketingLiveEditorStepReview } from "@/features/LiveFormEditor/Steps/StepReview/TicketingLiveEditorStepReview";
import { LiveTicketingData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";

type TicketingLiveFormEditorProps = {
  apiTicketing: TicketingOutput;
  isTicketingClosed: boolean;
};

export const TicketingLiveFormEditor: FC<TicketingLiveFormEditorProps> = ({
  apiTicketing: initialApiTicketing,
  isTicketingClosed,
}) => {
  const [liveFormData, setLiveFormData] = useState<LiveTicketingData | null>(null);
  const [apiFormObject, setApiFormObject] = useState<TicketingOutput>(initialApiTicketing);
  const [editOrCreateMode, setEditOrCreateMode] = useState<"edit" | "create">(
    initialApiTicketing.status === "draft" ? "create" : "edit"
  );

  useLogFormEditionStarted(initialApiTicketing, "ticketing");

  const featureFlags = useInitLiveFormFeatureFlags("ticketing", apiFormObject);

  const { steps } = useGetAllTicketingStepsInOrder({
    apiTicketing: apiFormObject,
    editOrCreateMode,
    isAddOnsStepOn: featureFlags.isAddOnsStepOn,
    isCampaignLifeCycleStepOn: featureFlags.isCampaignLifeCycleStepOn,
  });

  if (featureFlags.isAddOnsStepLoading || featureFlags.isCampaignLifeCycleStepLoading) {
    return null;
  }

  return (
    <CampaignLifeCycleChoiceProvider>
      <LiveFormEditorProvider
        setApiFormObject={setApiFormObject}
        setLiveFormData={setLiveFormData}
        steps={steps}
        liveFormData={liveFormData}
        apiFormObject={apiFormObject}
        editOrCreateMode={editOrCreateMode}
        setEditOrCreateMode={setEditOrCreateMode}
        formType="ticketing"
        formCategory={apiFormObject.formCategory}
        isTicketingClosed={isTicketingClosed}
        featureFlags={featureFlags}
      >
        {({ currentStepKey, previewType }) => (
          <LiveFormEditorBase
            slots={{
              steps: (
                <>
                  {currentStepKey === "campaign-life-cycle" && <TicketingLiveEditorStepCampaignLifeCycle />}
                  {currentStepKey === "title" && <TicketingLiveEditorStepTitle />}
                  {currentStepKey === "date" && <TicketingLiveEditorStepDate />}
                  {currentStepKey === "prizes" && <TicketingLiveEditorStepPrizes />}
                  {currentStepKey === "location" && <TicketingLiveEditorStepLocation />}
                  {currentStepKey === "fundraising-goal" && <TicketingLiveEditorStepFundraisingGoal />}
                  {currentStepKey === "description" && <StepDescriptionSelector />}
                  {currentStepKey === "color" && <TicketingLiveEditorStepColor />}
                  {currentStepKey === "banner" && <TicketingLiveEditorStepBanner />}
                  {currentStepKey === "logo" && <TicketingLiveEditorStepLogo />}
                  {currentStepKey === "order-fulfillment" && supportsFulfillment(apiFormObject.formCategory) && (
                    <TicketingLiveEditorStepOrderFulfillment />
                  )}
                  {currentStepKey === "tickets" && <TicketingLiveEditorStepTickets />}
                  {currentStepKey === "add-donation" && <TicketingLiveEditorStepAdditionalDonation />}
                  {currentStepKey === "add-ons" && <TicketingLiveEditorStepAddOns />}
                  {currentStepKey === "automated-communications" && <TicketingLiveEditorStepAutomatedCommunications />}
                  {currentStepKey === "advanced-settings" && <TicketingLiveEditorStepAdvancedSettings />}
                  {currentStepKey === "questions" && <TicketingLiveEditorStepQuestionsV4 />}
                  {currentStepKey === "discount-codes" && <TicketingLiveEditorStepDiscountCode />}
                  {currentStepKey === "business-model" && <LiveEditorStepBusinessModel />}
                  {currentStepKey === "e-ticket" && <TicketingLiveEditorStepETicket />}
                  {currentStepKey === "review" && <TicketingLiveEditorStepReview />}
                </>
              ),
              preview: (
                <>
                  {previewType === "form" && <TicketingLivePreview />}
                  {previewType === "email" && <TicketingEmailLivePreview />}
                  {previewType === "e-ticket" && <ETicketStepLivePreview />}
                  {previewType === "membership-card" && <MembershipCardLivePreview />}
                  {previewType === "campaign-life-cycle" && <CampaignLifeCycleIllustration />}
                </>
              ),
            }}
          />
        )}
      </LiveFormEditorProvider>
    </CampaignLifeCycleChoiceProvider>
  );
};
